import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, Car, Gavel, Calculator as CalcIcon, TrendingUp, Shield, Globe } from 'lucide-react';
import axios from 'axios';
import VehicleCard from '../components/VehicleCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Homepage = () => {
  const [featuredVehicles, setFeaturedVehicles] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    const fetchFeatured = async () => {
      try {
        const response = await axios.get(`${API}/vehicles/featured`);
        setFeaturedVehicles(response.data);
      } catch (error) {
        console.error('Error fetching featured vehicles:', error);
      }
    };
    fetchFeatured();
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      window.location.href = `/marketplace?search=${encodeURIComponent(searchQuery)}`;
    }
  };

  const categories = [
    { name: 'Sedan', type: 'sedan', image: 'https://images.unsplash.com/photo-1621007947382-bb3c3994e3fb?w=400' },
    { name: 'SUV', type: 'suv', image: 'https://images.unsplash.com/photo-1606664515524-ed2f786a0bd6?w=400' },
    { name: 'Truck', type: 'truck', image: 'https://images.unsplash.com/photo-1533473359331-0135ef1b58bf?w=400' },
    { name: 'Luxury', type: 'luxury', image: 'https://images.unsplash.com/photo-1618843479313-40f8afb4b4d8?w=400' },
    { name: 'Electric', type: 'electric', image: 'https://images.unsplash.com/photo-1560958089-b8a1929cea89?w=400' },
    { name: 'Salvage', type: 'salvage', image: 'https://images.unsplash.com/photo-1552519507-da3b142c6e3d?w=400' },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1759166499631-4591975f5b40?w=1920"
            alt="Luxury cars"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent to-background"></div>
        </div>

        <div className="relative z-10 text-center max-w-5xl mx-auto px-4">
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-heading font-black mb-6 leading-tight" data-testid="hero-title">
            PREMIUM AUTO AUCTIONS
            <span className="block text-primary mt-2">WORLDWIDE DELIVERY</span>
          </h1>
          <p className="text-lg sm:text-xl text-textSecondary mb-8 max-w-2xl mx-auto">
            Buy and bid on quality vehicles from the comfort of your home. Trusted dealership with global shipping to Nigeria.
          </p>

          {/* Search Bar */}
          <form onSubmit={handleSearch} className="max-w-3xl mx-auto mb-8">
            <div className="glass rounded-sm p-2 flex items-center">
              <Search className="w-6 h-6 text-textSecondary mx-3" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by make, model, or year..."
                data-testid="search-input"
                className="flex-1 bg-transparent border-none outline-none text-white placeholder-textSecondary px-2 py-3"
              />
              <button
                type="submit"
                data-testid="search-button"
                className="bg-primary hover:bg-primaryHover text-white rounded-sm font-bold tracking-wide uppercase px-6 py-3 transition-all duration-300 red-glow"
              >
                Search
              </button>
            </div>
          </form>

          {/* CTA Buttons */}
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/marketplace"
              data-testid="browse-cars-button"
              className="bg-primary hover:bg-primaryHover text-white rounded-sm font-bold tracking-wide uppercase px-8 py-4 transition-all duration-300 red-glow"
            >
              <Car className="inline w-5 h-5 mr-2" />
              Browse Cars
            </Link>
            <Link
              to="/auctions"
              data-testid="join-auctions-button"
              className="bg-transparent border-2 border-primary text-white hover:bg-primary rounded-sm font-bold tracking-wide uppercase px-8 py-4 transition-colors"
            >
              <Gavel className="inline w-5 h-5 mr-2" />
              Join Auctions
            </Link>
            <Link
              to="/request-vehicle"
              data-testid="request-vehicle-button"
              className="bg-transparent border border-white/20 text-white hover:bg-white/10 rounded-sm font-bold tracking-wide uppercase px-8 py-4 transition-colors"
            >
              <Car className="inline w-5 h-5 mr-2" />
              Request a Car
            </Link>
          </div>
        </div>
      </section>

      {/* Categories Section */}
      <section className="py-20 bg-surface">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-heading font-bold mb-4" data-testid="categories-heading">Browse by Category</h2>
            <p className="text-textSecondary">Find your perfect vehicle from our diverse collection</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {categories.map((category) => (
              <Link
                key={category.type}
                to={`/marketplace?body_type=${category.type}`}
                data-testid={`category-${category.type}`}
                className="group relative h-48 rounded-sm overflow-hidden"
              >
                <img
                  src={category.image}
                  alt={category.name}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                />
                <div className="absolute inset-0 bg-black/50 group-hover:bg-black/30 transition-colors" />
                <div className="absolute inset-0 flex items-center justify-center">
                  <h3 className="text-2xl font-heading font-bold text-white">{category.name}</h3>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Vehicles */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-heading font-bold mb-4" data-testid="featured-vehicles-heading">Featured Auctions</h2>
            <p className="text-textSecondary">Premium vehicles ending soon</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {featuredVehicles.slice(0, 8).map((vehicle) => (
              <VehicleCard key={vehicle.vehicle_id} vehicle={vehicle} />
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/marketplace"
              className="bg-primary hover:bg-primaryHover text-white rounded-sm font-bold tracking-wide uppercase px-8 py-3 inline-block transition-all duration-300 red-glow"
            >
              View All Vehicles
            </Link>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-surface">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-heading font-bold mb-4">How It Works</h2>
            <p className="text-textSecondary">Simple steps to own your dream vehicle</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-surfaceHighlight p-8 rounded-sm text-center">
              <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center mx-auto mb-6">
                <Search className="w-8 h-8" />
              </div>
              <h3 className="text-xl font-heading font-bold mb-4">Browse & Select</h3>
              <p className="text-textSecondary">Search through thousands of quality vehicles and find the perfect match</p>
            </div>

            <div className="bg-surfaceHighlight p-8 rounded-sm text-center">
              <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center mx-auto mb-6">
                <Gavel className="w-8 h-8" />
              </div>
              <h3 className="text-xl font-heading font-bold mb-4">Place Your Bid</h3>
              <p className="text-textSecondary">Participate in live auctions or buy directly at competitive prices</p>
            </div>

            <div className="bg-surfaceHighlight p-8 rounded-sm text-center">
              <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center mx-auto mb-6">
                <Globe className="w-8 h-8" />
              </div>
              <h3 className="text-xl font-heading font-bold mb-4">Ship to Nigeria</h3>
              <p className="text-textSecondary">We handle shipping, customs, and delivery right to your doorstep</p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-heading font-bold mb-4">Why Choose JAMOH & CO</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="flex items-start space-x-4">
              <div className="w-12 h-12 bg-primary/20 rounded-sm flex items-center justify-center flex-shrink-0">
                <Shield className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h3 className="text-xl font-heading font-bold mb-2">Trusted Platform</h3>
                <p className="text-textSecondary">Verified dealers and secure transactions for your peace of mind</p>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <div className="w-12 h-12 bg-primary/20 rounded-sm flex items-center justify-center flex-shrink-0">
                <TrendingUp className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h3 className="text-xl font-heading font-bold mb-2">Competitive Prices</h3>
                <p className="text-textSecondary">Get the best deals through our live auction system</p>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <div className="w-12 h-12 bg-primary/20 rounded-sm flex items-center justify-center flex-shrink-0">
                <Globe className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h3 className="text-xl font-heading font-bold mb-2">Global Shipping</h3>
                <p className="text-textSecondary">Reliable worldwide delivery with full customs support</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Homepage;